import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  IconButton,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
} from "@mui/icons-material";
import { uploadOpinionImage, deleteOpinionImage, updateOpinionImageUrl } from "../../services/api";

interface ImageUploadProps {
  opinionId: string;
  currentImageUrl?: string;
  onImageUpdate: (imageUrl: string | null) => void;
}

const ImageUpload: React.FC<ImageUploadProps> = ({ opinionId, currentImageUrl, onImageUpdate }) => {
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(currentImageUrl || null);
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // propsのcurrentImageUrlが変更されたらpreviewUrlを更新
  useEffect(() => {
    setPreviewUrl(currentImageUrl || null);
  }, [currentImageUrl]);

  const uploadFile = async (file: File) => {
    setError(null);
    setUploading(true);

    try {
      const downloadUrl = await uploadOpinionImage(opinionId, file);
      await updateOpinionImageUrl(opinionId, downloadUrl);
      setPreviewUrl(downloadUrl);
      onImageUpdate(downloadUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : "アップロードに失敗しました");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadFile(file);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);

    const file = event.dataTransfer.files[0];
    if (!file) return;

    const acceptedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!acceptedTypes.includes(file.type)) {
      setError("対応していないファイル形式です。JPG, PNG, GIF, WebPのみアップロードできます");
      return;
    }

    await uploadFile(file);
  };

  const handleDelete = async () => {
    if (!previewUrl) return;

    setError(null);
    setDeleting(true);

    try {
      // 1. Storage から削除
      await deleteOpinionImage(opinionId, previewUrl);

      // 2. Firestore の imageUrl を削除
      await updateOpinionImageUrl(opinionId, null);

      // 3. UI を更新
      setPreviewUrl(null);
      onImageUpdate(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "削除に失敗しました");
    } finally {
      setDeleting(false);
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          イメージ画像
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          問題に関連するイメージ画像をアップロードできます（任意）
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />

        {previewUrl ? (
          // 画像プレビュー
          <Box>
            <Box
              sx={{
                position: "relative",
                display: "inline-block",
                maxWidth: 400,
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <img
                src={previewUrl}
                alt="問題のイメージ"
                style={{
                  maxWidth: "100%",
                  height: "auto",
                  display: "block",
                }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  bgcolor: "rgba(255,255,255,0.9)",
                  "&:hover": {
                    bgcolor: "rgba(255,255,255,1)",
                  },
                }}
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? <CircularProgress size={20} /> : <DeleteIcon color="error" />}
              </IconButton>
            </Box>
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={uploading ? <CircularProgress size={16} /> : <UploadIcon />}
                onClick={handleButtonClick}
                disabled={uploading}
              >
                {uploading ? "アップロード中..." : "画像を変更"}
              </Button>
            </Box>
          </Box>
        ) : (
          // アップロードボタン（ドラッグ&ドロップ対応）
          <Box
            sx={{
              border: "2px dashed",
              borderColor: isDragOver ? "primary.main" : "grey.300",
              borderRadius: 1,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              bgcolor: isDragOver ? "primary.50" : "transparent",
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "grey.50",
              },
            }}
            onClick={handleButtonClick}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            {uploading ? (
              <CircularProgress />
            ) : (
              <>
                <ImageIcon
                  sx={{ fontSize: 48, color: isDragOver ? "primary.main" : "grey.400", mb: 1 }}
                />
                <Typography variant="body1" color={isDragOver ? "primary.main" : "text.secondary"}>
                  {isDragOver
                    ? "ここにドロップして画像をアップロード"
                    : "クリックまたはドラッグ&ドロップで画像を選択"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  対応形式: JPG, PNG, GIF, WebP（5MB以下）
                </Typography>
              </>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default ImageUpload;
